/**
 * Data-quality indicator (spec sections 22 & 34).
 *
 * Carries a word, not just a colour, and its tooltip names the exact missing
 * fields rather than saying "incomplete".
 */

import type { DataQuality } from '@/types/product';
import { QUALITY_DESCRIPTIONS, QUALITY_LABELS } from '@/lib/providers/quality';

const CLASS_FOR: Record<DataQuality['grade'], string> = {
  complete: 'badge-good',
  partial: 'badge-neutral',
  minimal: 'badge-warn',
  unusable: 'badge-critical',
};

export function DataQualityBadge({ quality }: { quality: DataQuality }): React.JSX.Element {
  const detail = [
    QUALITY_DESCRIPTIONS[quality.grade],
    quality.missingFields.length > 0 ? `Missing: ${quality.missingFields.join(', ')}.` : null,
    ...quality.warnings,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={`badge ${CLASS_FOR[quality.grade]}`} title={detail}>
      Data: {QUALITY_LABELS[quality.grade]}
      {quality.stale && ' · stale'}
    </span>
  );
}

/**
 * Absolute date and time, in the reader's own locale and timezone.
 *
 * Always absolute — "3 days ago" alone forces the reader to work out whether
 * that predates the weekly price change they care about.
 */
function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** How long ago, in the coarsest unit that is still honest. */
function ageOf(iso: string): string | null {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 0) return null;
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 31) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

/** "21 Aug 2026, 13:05 · 2 days ago" — the stamp plus its age. */
function retrievedLabel(iso: string): string {
  const age = ageOf(iso);
  return age === null ? formatStamp(iso) : `${formatStamp(iso)} · ${age}`;
}

/** Expanded panel for the product detail page. */
export function DataQualityPanel({
  quality,
  sources,
  lastUpdated,
}: {
  quality: DataQuality;
  sources: Array<{ label: string; provider: string; retrievedAt: string }>;
  lastUpdated: string;
}): React.JSX.Element {
  return (
    <section className="panel">
      <h2 className="panel-title">Data quality &amp; sources</h2>

      <dl className="kv-list">
        <div className="kv-row">
          <dt>Completeness</dt>
          <dd>
            {QUALITY_LABELS[quality.grade]} ({Math.round(quality.completeness * 100)}%)
          </dd>
        </div>
        {sources.map((s) => (
          <div className="kv-row" key={`${s.label}-${s.provider}`}>
            <dt>{s.label}</dt>
            <dd>
              {s.provider}
              <span className="kv-when">{retrievedLabel(s.retrievedAt)}</span>
            </dd>
          </div>
        ))}
        <div className="kv-row">
          <dt>Record updated</dt>
          <dd>
            {formatStamp(lastUpdated)}
            <span className="kv-when">
              when we last wrote this row — see each source above for when its
              figures were actually read
            </span>
          </dd>
        </div>
      </dl>

      {quality.missingFields.length > 0 && (
        <div className="notice notice-warn" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
          <div>
            <strong>Missing data.</strong> These fields were not available, so any metric that
            depends on them is shown as N/A rather than estimated:
            <ul>
              {quality.missingFields.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {quality.warnings.length > 0 && (
        <div className="notice notice-info" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
          <div>
            <strong>How this data was derived.</strong>
            <ul>
              {quality.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}
